import React from "react";

import {Dialog, Transition} from "@headlessui/react";
import {
    Bars3Icon,
    Cog6ToothIcon,
    CreditCardIcon,
    DocumentDuplicateIcon,
    HomeIcon,
    QueueListIcon,
    TableCellsIcon,
    UserCircleIcon,
    XMarkIcon,
} from "@heroicons/react/24/outline";

import * as graphics from "@client/app/graphics";
import * as analytics from "@client/shared/analytics";

import {Icon} from "@client/components/Icon";
import {Shell} from "@client/components/Shell";
import type {Breadcrumbs} from "@client/site/ManagerLayout";
import {useSiteContext} from "@client/site/context";
import {reverse} from "@reactivated";

const Breadcrumbs = (props: {crumbs: Breadcrumbs}) => {
    const crumbs = [...props.crumbs];
    const current = false as boolean;

    analytics.useTrackPageView();

    return (
        <nav className="flex border-b border-gray-200 bg-white" aria-label="Breadcrumb">
            <ol
                role="list"
                className="
                    mx-auto flex w-full max-w-(--breakpoint-xl) space-x-4 px-4
                    sm:px-6
                    lg:px-8
                "
            >
                <li className="flex">
                    {/* h-12 for when the home icon is by itself without any crumbs */}
                    <div className="flex h-12 items-center">
                        <a
                            href={reverse("clients_list")}
                            className="
                                text-gray-800
                                hover:text-gray-400
                            "
                        >
                            <HomeIcon className="size-6 shrink-0" aria-hidden="true" />
                            <span className="sr-only">Home</span>
                        </a>
                    </div>
                </li>
                {crumbs.map((page, index) => (
                    <li key={index} className="flex">
                        <div className="flex items-center">
                            <svg
                                className="h-full w-6 shrink-0 text-gray-300"
                                viewBox="0 0 24 44"
                                preserveAspectRatio="none"
                                fill="currentColor"
                                aria-hidden="true"
                            >
                                <path d="M.293 0l22 22-22 22h1.414l22-22-22-22H.293z" />
                            </svg>
                            {"label" in page ? (
                                <span
                                    className="
                                        ml-6 max-w-[300px] truncate text-sm font-medium
                                        text-gray-800
                                        hover:text-gray-400
                                    "
                                    aria-current={current ? "page" : undefined}
                                >
                                    {page.label}
                                </span>
                            ) : "link" in page ? (
                                <a
                                    href={page.link}
                                    className="
                                        ml-6 max-w-[300px] truncate text-sm font-medium
                                        text-gray-800
                                        hover:text-gray-400
                                    "
                                    aria-current={current ? "page" : undefined}
                                >
                                    {page.title}
                                </a>
                            ) : (
                                <button
                                    type="button"
                                    onClick={page.onClick}
                                    className="
                                        ml-6 max-w-[300px] truncate text-sm font-medium
                                        text-gray-800
                                        hover:text-gray-400
                                    "
                                    aria-current={current ? "page" : undefined}
                                >
                                    {page.title}
                                </button>
                            )}
                        </div>
                    </li>
                ))}
            </ol>
        </nav>
    );
};

function classNames(...classes: string[]) {
    return classes.filter(Boolean).join(" ");
}

export const DumbbellIcon = () => (
    <Icon large className="mt-0.5 ml-1" icon="custom-dumbbell" />
);

export const ManagerShell = (props: {
    children: React.ReactNode;
    currentClientManagerId: number | null;
    breadCrumbs?: Breadcrumbs;
}) => {
    const {clients: contextClients, profile, request} = useSiteContext();
    const clients = contextClients.map((client) => ({
        ...client,
        href: reverse("client_detail", {client_manager_id: client.id}),
        current: client.id == props.currentClientManagerId,
        initial: client.name[0],
    }));
    const navigation = [
        {
            name: "Dashboard",
            href: reverse("clients_list"),
            icon: HomeIcon,
            current: false,
        },
        {
            name: "Programs",
            href: reverse("programs_list"),
            icon: TableCellsIcon,
            current: false,
        },
        {
            name: "Workouts",
            href: reverse("workouts"),
            icon: DumbbellIcon,
            current: false,
        },
        {
            name: "Meal Plans",
            href: reverse("meal_plans_list"),
            icon: DocumentDuplicateIcon,
            current: false,
        },
        {
            name: "Settings",
            href: reverse("clients_settings"),
            icon: Cog6ToothIcon,
            current: false,
        },
        {
            name: "Billing",
            href: reverse("business_billing"),
            icon: CreditCardIcon,
            current: false,
        },
        {
            name: "Journal",
            href: reverse("journal"),
            icon: QueueListIcon,
            current: false,
        },
    ].map((item, index) => ({
        ...item,
        current:
            request.path == item.href ||
            (index != 0 && request.path.startsWith(item.href)),
    }));
    const [sidebarOpen, setSidebarOpen] = React.useState(false);

    return (
        <Shell htmlClassName="h-full bg-stone-50" bodyClassName="h-full">
            <Transition.Root show={sidebarOpen} as={React.Fragment}>
                <Dialog
                    as="div"
                    className="
                        relative z-50
                        lg:hidden
                    "
                    onClose={setSidebarOpen}
                >
                    <Transition.Child
                        as={React.Fragment}
                        enter="transition-opacity ease-linear duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="transition-opacity ease-linear duration-300"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-gray-900/80" />
                    </Transition.Child>

                    <div className="fixed inset-0 flex">
                        <Transition.Child
                            as={React.Fragment}
                            enter="transition ease-in-out duration-300 transform"
                            enterFrom="-translate-x-full"
                            enterTo="translate-x-0"
                            leave="transition ease-in-out duration-300 transform"
                            leaveFrom="translate-x-0"
                            leaveTo="-translate-x-full"
                        >
                            <Dialog.Panel className="relative mr-16 flex w-full max-w-xs flex-1">
                                <Transition.Child
                                    as={React.Fragment}
                                    enter="ease-in-out duration-300"
                                    enterFrom="opacity-0"
                                    enterTo="opacity-100"
                                    leave="ease-in-out duration-300"
                                    leaveFrom="opacity-100"
                                    leaveTo="opacity-0"
                                >
                                    <div
                                        className="
                                            absolute top-0 left-full flex w-16
                                            justify-center pt-5
                                        "
                                    >
                                        <button
                                            type="button"
                                            className="-m-2.5 p-2.5"
                                            onClick={() => setSidebarOpen(false)}
                                        >
                                            <span className="sr-only">
                                                Close sidebar
                                            </span>
                                            <XMarkIcon
                                                className="size-6 text-white"
                                                aria-hidden="true"
                                            />
                                        </button>
                                    </div>
                                </Transition.Child>
                                {/* Sidebar component, swap this element with another sidebar if you like */}
                                <div
                                    className="
                                        flex grow flex-col gap-y-5 overflow-y-auto
                                        bg-white px-6 pb-2
                                    "
                                >
                                    <div className="flex h-16 shrink-0 items-center">
                                        <graphics.Logo
                                            className="h-8 w-auto"
                                            title="Joy"
                                        />
                                    </div>
                                    <nav className="flex flex-1 flex-col">
                                        <ul
                                            role="list"
                                            className="flex flex-1 flex-col gap-y-7"
                                        >
                                            <li>
                                                <ul
                                                    role="list"
                                                    className="-mx-2 space-y-1"
                                                >
                                                    {navigation.map((item) => (
                                                        <li key={item.name}>
                                                            <a
                                                                href={item.href}
                                                                className={classNames(
                                                                    item.current
                                                                        ? "bg-gray-50 text-indigo-600"
                                                                        : "text-gray-700 hover:text-indigo-600 hover:bg-gray-50",
                                                                    "group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold",
                                                                )}
                                                            >
                                                                <item.icon
                                                                    className={classNames(
                                                                        item.current
                                                                            ? "text-indigo-600"
                                                                            : "text-gray-400 group-hover:text-indigo-600",
                                                                        "h-6 w-6 shrink-0",
                                                                    )}
                                                                    aria-hidden="true"
                                                                />
                                                                {item.name}
                                                            </a>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </li>
                                            <li>
                                                <div
                                                    className="
                                                        text-xs/6 font-semibold
                                                        text-gray-400
                                                    "
                                                >
                                                    Your clients
                                                </div>
                                                <ul
                                                    role="list"
                                                    className="-mx-2 mt-2 space-y-1"
                                                >
                                                    {clients.map((client) => (
                                                        <li key={client.name}>
                                                            <a
                                                                href={client.href}
                                                                className={classNames(
                                                                    client.current
                                                                        ? "bg-gray-50 text-indigo-600"
                                                                        : "text-gray-700 hover:text-indigo-600 hover:bg-gray-50",
                                                                    "group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold",
                                                                )}
                                                            >
                                                                <span
                                                                    className={classNames(
                                                                        client.current
                                                                            ? "text-indigo-600 border-indigo-600"
                                                                            : "text-gray-400 border-gray-200 group-hover:border-indigo-600 group-hover:text-indigo-600",
                                                                        "flex h-6 w-6 shrink-0 items-center justify-center rounded-lg border text-[0.625rem] font-medium bg-white",
                                                                    )}
                                                                >
                                                                    {client.initial}
                                                                </span>
                                                                <span className="truncate">
                                                                    {client.name}
                                                                </span>
                                                            </a>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </li>
                                        </ul>
                                    </nav>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </Dialog>
            </Transition.Root>

            {/* Static sidebar for desktop */}
            <div
                className="
                    hidden
                    lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-60 lg:flex-col
                "
            >
                {/* Sidebar component, swap this element with another sidebar if you like */}
                <div
                    className="
                        flex grow flex-col gap-y-5 overflow-y-auto border-r
                        border-gray-200 bg-white px-6
                    "
                >
                    <div className="flex h-16 shrink-0 items-center">
                        <graphics.Logo className="h-8 w-auto" title="Joy" />
                    </div>
                    <nav className="flex flex-1 flex-col">
                        <ul role="list" className="flex flex-1 flex-col gap-y-7">
                            <li>
                                <ul role="list" className="-mx-2 space-y-1">
                                    {navigation.map((item) => (
                                        <li key={item.name}>
                                            <a
                                                href={item.href}
                                                className={classNames(
                                                    item.current
                                                        ? "bg-gray-50 text-indigo-600"
                                                        : "text-gray-700 hover:text-indigo-600 hover:bg-gray-50",
                                                    "group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold",
                                                )}
                                            >
                                                <item.icon
                                                    className={classNames(
                                                        item.current
                                                            ? "text-indigo-600"
                                                            : "text-gray-400 group-hover:text-indigo-600",
                                                        "h-6 w-6 shrink-0",
                                                    )}
                                                    aria-hidden="true"
                                                />
                                                {item.name}
                                            </a>
                                        </li>
                                    ))}
                                </ul>
                            </li>
                            <li>
                                <div className="text-xs/6 font-semibold text-gray-400">
                                    Your clients
                                </div>
                                <ul role="list" className="-mx-2 mt-2 space-y-1">
                                    {clients.map((client) => (
                                        <li key={client.name}>
                                            <a
                                                href={client.href}
                                                className={classNames(
                                                    client.current
                                                        ? "bg-gray-50 text-indigo-600"
                                                        : "text-gray-700 hover:text-indigo-600 hover:bg-gray-50",
                                                    "group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold",
                                                )}
                                            >
                                                <span
                                                    className={classNames(
                                                        client.current
                                                            ? "text-indigo-600 border-indigo-600"
                                                            : "text-gray-400 border-gray-200 group-hover:border-indigo-600 group-hover:text-indigo-600",
                                                        "flex h-6 w-6 shrink-0 items-center justify-center rounded-lg border text-[0.625rem] font-medium bg-white",
                                                    )}
                                                >
                                                    {client.initial}
                                                </span>
                                                <span className="truncate">
                                                    {client.name}
                                                </span>
                                            </a>
                                        </li>
                                    ))}
                                </ul>
                            </li>
                            <li className="-mx-6 mt-auto">
                                <a
                                    href={reverse("profile")}
                                    className="
                                        flex items-center gap-x-4 px-6 py-3 text-sm/6
                                        font-semibold text-gray-900
                                        hover:bg-gray-50
                                    "
                                >
                                    <div
                                        aria-hidden="true"
                                        className="
                                            size-8 rounded-full bg-indigo-50 p-0.5
                                        "
                                    >
                                        <UserCircleIcon />
                                    </div>
                                    <span className="sr-only">Your profile</span>
                                    <span aria-hidden="true">
                                        {profile?.user.manager_full_name}
                                    </span>
                                </a>
                            </li>
                        </ul>
                    </nav>
                </div>
            </div>

            <div
                className="
                    sticky top-0 z-40 flex items-center gap-x-6 bg-white p-4 shadow-xs
                    sm:px-6
                    lg:hidden
                "
            >
                <button
                    type="button"
                    className="
                        -m-2.5 p-2.5 text-gray-700
                        lg:hidden
                    "
                    onClick={() => setSidebarOpen(true)}
                >
                    <span className="sr-only">Open sidebar</span>
                    <Bars3Icon className="size-6" aria-hidden="true" />
                </button>
                <div className="flex-1 text-sm/6 font-semibold text-gray-900">
                    Dashboard
                </div>
                <a href={reverse("profile")}>
                    <span className="sr-only">Your profile</span>
                    <div
                        aria-hidden="true"
                        className="size-8 rounded-full bg-indigo-50 p-0.5"
                    >
                        <UserCircleIcon />
                    </div>
                </a>
            </div>

            <div
                className="
                    hidden pl-60
                    lg:block
                "
            >
                <Breadcrumbs crumbs={props.breadCrumbs ?? []} />
            </div>
            <main
                className="
                    flex flex-1 flex-col py-10
                    lg:pl-60
                "
            >
                <div
                    className="
                        flex flex-1 flex-col px-4
                        sm:px-6
                        lg:px-8
                    "
                >
                    {props.children}
                </div>
            </main>
        </Shell>
    );
};
